
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });

    // Listen to checkbox changes globally (desktop and mobile)
    document.addEventListener('change', (e) => {
      const checkbox = e.target;
      if (!checkbox.matches('.facets__disclosure input[type=checkbox]')) return;

      const form = checkbox.closest('form');
      if (!form) return;

      const searchParams = new URLSearchParams(new FormData(form)).toString();
      FacetFiltersForm.renderPage(searchParams, e);
    });

    // Filter toggle for mobile
    document.addEventListener('DOMContentLoaded', () => {
      const toggleButton = document.querySelector('.mobile-facets__open, .collection-filters__disclosure-button');
      const filterFormMobile = document.getElementById('FacetFiltersFormMobile');

      if (toggleButton && filterFormMobile) {
        filterFormMobile.style.display = 'none';

        toggleButton.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = filterFormMobile.style.display === 'block';
          filterFormMobile.style.display = isOpen ? 'none' : 'block';
        });
      }
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    document.getElementById('main-collection-product-grid').classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = el => el.url === url;
      const cached = FacetFiltersForm.filterData.find(filterDataUrl);

      if (cached) {
        FacetFiltersForm.renderFilters(cached.html, event);
        FacetFiltersForm.renderProductGrid(cached.html);
        FacetFiltersForm.renderProductCount(cached.html);
      } else {
        fetch(url)
          .then(res => res.text())
          .then(html => {
            FacetFiltersForm.filterData.push({ html, url });
            FacetFiltersForm.renderFilters(html, event);
            FacetFiltersForm.renderProductGrid(html);
            FacetFiltersForm.renderProductCount(html);
          });
      }
    });

    if (updateURLHash) {
      history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }
  }

  static renderFilters(html, event) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const facetEls = parsed.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

    const prevOpen = Array.from(document.querySelectorAll('.js-filter details[open]'))
      .map(el => el.closest('.js-filter')?.dataset?.index);

    const matchesIndex = (el) => {
      const jsFilter = event?.target.closest('.js-filter');
      return jsFilter ? (el.dataset.index === jsFilter.dataset.index && !el.hasAttribute('data-and-operator')) : false;
    };

    const facetsToRender = Array.from(facetEls).filter(el => !matchesIndex(el));
    const countsToRender = Array.from(facetEls).find(matchesIndex);

    facetsToRender.forEach((el) => {
      const container = document.querySelector(`.js-filter[data-index="${el.dataset.index}"]`);
      if (container) {
        container.innerHTML = el.innerHTML;

        if (prevOpen.includes(el.dataset.index)) {
          container.querySelector('details')?.setAttribute('open', '');
        }
      }
    });

    if (countsToRender) {
      const target = event?.target.closest('.js-filter');
      if (target) {
        FacetFiltersForm.renderCounts(countsToRender, target);
      }
    }

    FacetFiltersForm.renderActiveFacets(parsed);
  }

  static renderProductGrid(html) {
    const dom = new DOMParser().parseFromString(html, 'text/html');
    const grid = dom.getElementById('CollectionProductGrid');
    if (!grid) return;
    const newHTML = grid.innerHTML;

    const target = document.getElementById('CollectionProductGrid');
    if (target) {
      target.innerHTML = newHTML;
      target.querySelectorAll('template').forEach(el => {
        el.closest('form')?.append(el.content.cloneNode(true));
      });
    }
  }

  static renderProductCount(html) {
    const dom = new DOMParser().parseFromString(html, 'text/html');
    const newCount = dom.getElementById('CollectionProductCount');
    if (newCount) {
      document.querySelectorAll('#CollectionProductCount').forEach(el => {
        el.innerHTML = newCount.innerHTML;
      });
    }
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach(selector => {
      const newEl = html.querySelector(selector);
      if (newEl && document.querySelector(selector)) {
        document.querySelector(selector).innerHTML = newEl.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const countSel = '.facets__selected';
    const sourceEl = source.querySelector(countSel);
    const targetEl = target.querySelector(countSel);
    if (sourceEl && targetEl) {
      targetEl.outerHTML = sourceEl.outerHTML;
    }
  }

  static getSections() {
    return [
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset.id
      }
    ];
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formEl = event.target.closest('form');
    if (!formEl) return;

    const forms = [];
    const isMobile = formEl.id === 'FacetFiltersFormMobile';

    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      const id = form.id;
      if (isMobile && id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      } else if (!isMobile && (id === 'FacetSortForm' || id === 'FacetFiltersForm' || id === 'FacetSortDrawerForm')) {
        form.querySelectorAll('.no-js-list')?.forEach(el => el.remove());
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.split('?')[1] || '';
    FacetFiltersForm.renderPage(url);
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    if (link) {
      link.setAttribute('role', 'button');
      link.addEventListener('click', this.closeFilter.bind(this));
      link.addEventListener('keyup', (e) => {
        if (e.code.toUpperCase() === 'SPACE') this.closeFilter(e);
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);



